/* eslint-disable no-nested-ternary */
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApiClient, useFetch } from '@marageti/z4-lib';
import {
  ChecklistItem, ScreeningResult, TripRequest, TripRequestStatus,
  TripMatch, ConversationSummaries,
} from '@marageti/z4-sdk/lib/travel';
import { getConversationSummaryByConversationId } from '../conversation-links/helpers';
import useScreeningCorrection from '../../pages/trip-request/screen/use-screening-correction';
import TripRequestActionBar from './trip-request-action-bar-view';

const disableMatchStatuses = [
  TripRequestStatus.PostponedStatus,
  TripRequestStatus.PendingStatus,
  TripRequestStatus.JunkStatus,
  TripRequestStatus.CanceledStatus,
  TripRequestStatus.ReferredStatus,
];

const getDisabledButtons = (data?: TripRequest) => {
  // If there is no data or assignedTo, disable all buttons
  if (!data || !data.assignedTo) {
    return {
      disableScreen: true,
      disableMatch: true,
    };
  }

  return {
    disableMatch: disableMatchStatuses.includes(data.status),
  };
};

const TripRequestActionBarContainer = ({
  tripRequestId,
  loading = false,
}: {
  tripRequestId?: string;
  loading?: boolean;
}) => {
  const apiClient = useApiClient();
  const [logCallModalOpen, setLogCallModalOpen] = useState(false);

  const { data: tripRequestData, isLoading: isLoadingTripRequest } = useFetch<TripRequest>(
    apiClient.tripRequestsClient.getTripRequest,
    [tripRequestId],
    !!tripRequestId,
  );
  const teamMemberAssignedTo = tripRequestData?.assignedTo;
  const travelerToZicassoId = tripRequestData?.travelerToZicassoId;

  const { data: tripMatchesData, isLoading: isLoadingTripMatch } = useFetch<{ results: TripMatch[] }>(
    apiClient.tripRequestsClient.getTripRequestTripMatches,
    [tripRequestId],
    !!tripRequestId,
  );
  const agentAssignedTo = useMemo(() => tripMatchesData?.results?.[0]?.assignedTo, [tripMatchesData]);

  const { data: agentSummaries, isLoading: isLoadingAgentSummaries } = useFetch<ConversationSummaries>(
    apiClient.tripRequestsClient.getConversationSummaries,
    [tripRequestId, { params: { person: agentAssignedTo } }],
    !!(tripRequestId && agentAssignedTo),
  );
  const agentContextSummary = useMemo(() => (
    travelerToZicassoId && agentSummaries
      ? getConversationSummaryByConversationId(travelerToZicassoId, agentSummaries)
      : undefined
  ), [agentSummaries, travelerToZicassoId]);

  const { data: teamMemberSummaries, isLoading: isLoadingTeamMemberSummaries } = useFetch<ConversationSummaries>(
    apiClient.tripRequestsClient.getConversationSummaries,
    [tripRequestId, { params: { person: teamMemberAssignedTo } }],
    !!(tripRequestId && teamMemberAssignedTo),
  );
  const teamMemberContextSummary = useMemo(() => (
    travelerToZicassoId && teamMemberSummaries
      ? getConversationSummaryByConversationId(travelerToZicassoId, teamMemberSummaries)
      : undefined
  ), [teamMemberSummaries, travelerToZicassoId]);

  const hasUnreadChatMessages = useMemo(() => (
    !!agentContextSummary?.hasNewMessages || !!teamMemberContextSummary?.hasNewMessages
  ), [agentContextSummary, teamMemberContextSummary]);

  const screeningResult = useFetch<ScreeningResult>(apiClient.screeningResultsClient.getScreeningResult, [tripRequestId], !!tripRequestId, {
    shouldRetryOnError: (error: any) => {
      if (error.code === 404) return false;
      return true;
    },
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });
  const screeningCorrection = useScreeningCorrection(screeningResult.data);
  const navigate = useNavigate();
  const {
    disableScreen,
    disableMatch,
  } = getDisabledButtons(tripRequestData);

  const isComponentLoading = isLoadingTripRequest
    || isLoadingTripMatch
    || isLoadingAgentSummaries
    || isLoadingTeamMemberSummaries
    || screeningCorrection.status === 'loading';

  if (!tripRequestData || loading) {
    return null;
  }

  const isScreened = !tripRequestData.checklist?.some((item) => item === ChecklistItem.NeedsScreening);
  const failedCriteria = screeningResult.data?.criteria?.filter((criterion) => !criterion.passed) ?? [];
  const failureReasonsCount = screeningCorrection.data?.effectiveFailureReasons?.length
    ?? failedCriteria.length;
  const screenMessage = isScreened
    ? 'Trip Previously Screened'
    : (failureReasonsCount > 0
      ? `Pre-Screen: ${failureReasonsCount} Failure Reasons`
      : 'Pre-Screen: No Failure Reasons');

  const notAssignedMessage = !tripRequestData?.assignedTo ? 'Please assign this trip request to continue' : '';

  return (
    <TripRequestActionBar
      loading={isComponentLoading}
      travelerToZicassoId={travelerToZicassoId!}
      disableScreen={disableScreen ?? false}
      disableMatch={disableMatch ?? false}
      logCallModalOpen={logCallModalOpen}
      onLogCall={() => setLogCallModalOpen(true)}
      onLogCallClose={() => setLogCallModalOpen(false)}
      onMatch={() => navigate(`/trip-requests/${tripRequestId}/match`)}
      onScreen={() => navigate(`/trip-requests/${tripRequestId}/screen`)}
      onChat={() => navigate(`./messages/${travelerToZicassoId}`)}
      notAssignedMessage={notAssignedMessage}
      isScreened={isScreened}
      screenMessage={screenMessage}
      screenFailureReasons={failureReasonsCount}
      hasUnreadChatMessages={hasUnreadChatMessages}
    />
  );
};

export default TripRequestActionBarContainer;
